import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { api } from "@data-phone/react-resource-server-auth";
import { RootState } from "../store"; 

export type DirectoryType =
  | "conference"
  | "extension"
  | "fax_server"
  | "flow"
  | "ivr_menu"
  | "paging_group"
  | "queue"
  | "ring_group";

export interface DirectoryItem {
  id: string;
  name: string;
  extension: string;
}

interface DirectoryStateItem {
  items: DirectoryItem[];
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

interface DirectoryState {
  [key: string]: DirectoryStateItem;
}

type RawDirectoryResponse = {
  [key in DirectoryType]?: {
    id: string;
    name?: string;
    extension?: string;
  }[];
};

const initialState: DirectoryState = {
  conference: { items: [], status: "idle", error: null },
  extension: { items: [], status: "idle", error: null },
  fax_server: { items: [], status: "idle", error: null },
  flow: { items: [], status: "idle", error: null },
  ivr_menu: { items: [], status: "idle", error: null },
  paging_group: { items: [], status: "idle", error: null },
  queue: { items: [], status: "idle", error: null },
  ring_group: { items: [], status: "idle", error: null },
};

export const fetchDirectoryData = createAsyncThunk<
  RawDirectoryResponse,
  void,
  { rejectValue: string }
>("directory/fetchAll", async (_, { rejectWithValue }) => {
  try {
    const response = await api.get(
      `${process.env.REACT_APP_LIVE_CALL_URL}/api/apps`
    );

    if (
      !response ||
      typeof response !== "object" ||
      !response.result ||
      typeof response.result !== "object"
    ) {
      return rejectWithValue("Invalid response structure");
    }

    return response.result as RawDirectoryResponse;
  } catch (error: any) {
    return rejectWithValue(error?.message || "Failed to fetch directory data");
  }
});

const directorySlice = createSlice({
  name: "directory",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDirectoryData.pending, (state) => {
        Object.keys(state).forEach((key) => {
          state[key].status = "loading";
          state[key].error = null;
        });
      })
      .addCase(
        fetchDirectoryData.fulfilled,
        (state, action: PayloadAction<RawDirectoryResponse>) => {
          const result = action.payload;

          Object.entries(result).forEach(([key, value]) => {
            if (key in state && Array.isArray(value)) {
              state[key].items = value.map((item) => ({
                id: item.id,
                name: item.name || item.extension || "Unknown",
                extension: item.extension || "",
              }));
              state[key].status = "succeeded";
              state[key].error = null;
            }
          });
        }
      )
      .addCase(fetchDirectoryData.rejected, (state, action) => {
        const errorMessage =
          typeof action.payload === "string"
            ? action.payload
            : action.error.message || "Failed to fetch directory data";

        Object.keys(state).forEach((key) => {
          state[key].status = "failed";
          state[key].error = errorMessage;
        });
      });
  },
});

export default directorySlice.reducer;

export const selectDirectoryByType = (state: RootState, type: DirectoryType): DirectoryItem[] =>
  state.directory[type]?.items || [];
